/**
 * MOS Audit Federation Settings panel.
 *
 * Operator-facing surface for the /api/integrations/mos-audit-federation
 * routes shipped in Piece 3 of the audit federation UI slice. Wraps:
 *   - GET status (vault + runtime)
 *   - PUT update (with optional MOS propagation)
 *   - POST /test (probe through the live forwarder)
 *   - DELETE (clear vault + best-effort MOS delete)
 *   - POST /generate-secret (one-shot 32-byte hex)
 *
 * Hygiene rules:
 *   - hmacSecret starts EMPTY on mount. The status response never carries it.
 *   - When `hasSecret` is already true on the server, the operator sees a
 *     hint telling them they must paste/generate a new value to save changes
 *     (the server schema requires hmacSecret on every PUT — see the
 *     "Decision on no-change PUT" note in the panel doc-comment).
 *   - The secret is never echoed to console / logs / error overlays.
 *   - `defaultValue` is NOT used for the secret input — it's a controlled field.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Eye, EyeOff, KeyRound, Sparkles, Trash2, PlayCircle, Save } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  getMosAuditFederationStatus,
  updateMosAuditFederation,
  testMosAuditFederation,
  disableMosAuditFederation,
  generateMosAuditSecret,
  type MosFederationStatusResponse,
  type MosFederationUpdateBody,
  type MosFederationTestResponse,
} from '@/lib/api';

const QUERY_KEY = ['mosAuditFederationStatus'] as const;

const DEFAULT_SERVICE_ID = 'maestro-audit-forwarder';
const DEFAULT_FORWARD_URL = 'https://apps.mahoosuc.ai/api/audit/events';

const SERVICE_ID_REGEX = /^[a-zA-Z0-9_-]+$/;
const HEX_REGEX = /^[a-fA-F0-9]+$/;

interface StatusPillProps {
  status: MosFederationStatusResponse | undefined;
  isLoading: boolean;
}

/**
 * Status pill colors map to the five operator-visible states:
 *
 *   - Active             : vault config + runtime forwarder both up
 *   - Disabled (neutral) : operator intentionally set authMode='disabled' —
 *                          the server sets runtimeReason for this case but it
 *                          is not a configuration error; show neutral gray
 *   - Configured (amber) : vault config present but runtime not enabled for
 *                          any other reason with no runtimeReason
 *   - Error              : vault config present + runtime disabled with a
 *                          non-empty `runtimeReason` that is NOT the
 *                          intentional authMode=disabled path
 *   - Not configured     : vault is empty
 */
function StatusPill({ status, isLoading }: StatusPillProps): React.ReactElement {
  if (isLoading || !status) {
    return (
      <Badge variant="outline" className="text-xs">
        Loading…
      </Badge>
    );
  }
  if (!status.enabled) {
    return (
      <Badge variant="secondary" className="text-xs">
        Not configured
      </Badge>
    );
  }
  if (status.runtimeEnabled) {
    return <Badge className="bg-success text-white text-xs hover:bg-success">Active</Badge>;
  }
  // Distinguish intentional operator choice (authMode='disabled') from real
  // errors. The server sets runtimeReason for both, but only the latter should
  // render the destructive red pill that suggests something needs fixing.
  if (status.authMode === 'disabled') {
    return (
      <Badge variant="outline" className="text-xs text-text-secondary">
        Disabled (auth_mode=disabled)
      </Badge>
    );
  }
  if (status.runtimeReason) {
    return (
      <Badge variant="destructive" className="text-xs" title={status.runtimeReason}>
        Error: {status.runtimeReason}
      </Badge>
    );
  }
  return (
    <Badge className="bg-warning text-white text-xs hover:bg-warning">
      Configured but not enabled
    </Badge>
  );
}

interface FormState {
  serviceId: string;
  hmacSecret: string;
  forwardUrl: string;
  authMode: 'disabled' | 'mos-oidc';
  propagateToMos: boolean;
  mosBearerToken: string;
}

function initialFormState(status: MosFederationStatusResponse | undefined): FormState {
  return {
    serviceId: status?.serviceId ?? DEFAULT_SERVICE_ID,
    // SECRET HYGIENE: never pre-populate from the status response.
    // The server never returns it, and the form intentionally starts blank.
    hmacSecret: '',
    forwardUrl: status?.forwardUrl ?? DEFAULT_FORWARD_URL,
    authMode: status?.authMode ?? 'mos-oidc',
    propagateToMos: true,
    mosBearerToken: '',
  };
}

interface TestResultSummary {
  ok: boolean;
  httpStatus?: number;
  error?: string;
}

export function MosAuditFederationPanel(): React.ReactElement {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => initialFormState(undefined));
  const [showSecret, setShowSecret] = useState(false);
  const [justGenerated, setJustGenerated] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [testResult, setTestResult] = useState<TestResultSummary | null>(null);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getMosAuditFederationStatus,
  });

  // Sync server-known fields into the form when status loads / refreshes.
  // We do NOT sync the secret — that field is intentionally locally-controlled.
  useEffect(() => {
    if (!status) return;
    setForm(current => ({
      ...current,
      serviceId:
        current.serviceId === '' ? (status.serviceId ?? DEFAULT_SERVICE_ID) : current.serviceId,
      forwardUrl:
        current.forwardUrl === '' ? (status.forwardUrl ?? DEFAULT_FORWARD_URL) : current.forwardUrl,
      authMode: status.authMode ?? current.authMode,
    }));
  }, [status]);

  const generateMutation = useMutation({
    mutationFn: generateMosAuditSecret,
    onSuccess: data => {
      setForm(current => ({ ...current, hmacSecret: data.secret }));
      setJustGenerated(true);
      setShowSecret(false);
      setSaveError(null);
      setSaveSuccess(false);
    },
    onError: (err: Error) => {
      setSaveError(`Generate failed: ${err.message}`);
    },
  });

  const saveMutation = useMutation({
    mutationFn: (body: { update: MosFederationUpdateBody; bearer: string }) =>
      updateMosAuditFederation(body.update, body.bearer),
    onSuccess: () => {
      // Invalidate rather than setQueryData so the GET cache stays canonical
      // even if the PUT response schema ever diverges from the GET response
      // schema. Matches the SecureKeysPanel / SettingsPage convention.
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setSaveError(null);
      setSaveSuccess(true);
      setJustGenerated(false);
      // Clear sensitive locals after a successful save — they're now persisted.
      setForm(current => ({ ...current, hmacSecret: '', mosBearerToken: '' }));
      setShowSecret(false);
    },
    onError: (err: Error) => {
      // When MOS propagation fails (mos_propagation_failed), the local vault
      // write and forwarder reload have ALREADY succeeded. Surface a two-part
      // message so the operator knows their local config is safe and only the
      // MOS-side registration needs attention.
      const msg = err.message;
      if (msg.includes('mos_propagation_failed')) {
        setSaveError(
          'Local config saved and forwarder reloaded. MOS registration failed — ' +
            msg.replace(/mos_propagation_failed[^:]*:?\s*/i, '').trim()
        );
      } else {
        setSaveError(msg);
      }
      setSaveSuccess(false);
    },
  });

  const testMutation = useMutation({
    mutationFn: testMosAuditFederation,
    onSuccess: (data: MosFederationTestResponse) => {
      setTestResult({ ok: data.ok, httpStatus: data.httpStatus, error: data.error });
    },
    onError: (err: Error) => {
      setTestResult({ ok: false, error: err.message });
    },
  });

  const disableMutation = useMutation({
    mutationFn: (bearer: string) => disableMosAuditFederation(bearer),
    onSuccess: () => {
      // Invalidate rather than setQueryData — triggers a fresh GET so the
      // form re-syncs from server truth after vault clear. Matches convention.
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setForm(initialFormState(undefined));
      setShowSecret(false);
      setJustGenerated(false);
      setSaveError(null);
      setSaveSuccess(false);
      setTestResult(null);
    },
    onError: (err: Error) => {
      setSaveError(`Disable failed: ${err.message}`);
    },
  });

  const hasSecretOnServer = status?.hasSecret === true;
  const secretValid = HEX_REGEX.test(form.hmacSecret) && form.hmacSecret.length >= 32;
  const serviceIdValid =
    SERVICE_ID_REGEX.test(form.serviceId) &&
    form.serviceId.length >= 3 &&
    form.serviceId.length <= 64;
  let forwardUrlValid = false;
  try {
    new URL(form.forwardUrl);
    forwardUrlValid = true;
  } catch {
    forwardUrlValid = false;
  }
  const missingBearerForPropagation =
    form.propagateToMos && form.mosBearerToken.trim().length === 0;

  const canSave =
    serviceIdValid &&
    secretValid &&
    forwardUrlValid &&
    !missingBearerForPropagation &&
    !saveMutation.isPending;

  function handleSave(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSave) return;
    const update: MosFederationUpdateBody = {
      serviceId: form.serviceId.trim(),
      hmacSecret: form.hmacSecret,
      forwardUrl: form.forwardUrl.trim(),
      authMode: form.authMode,
      propagateToMos: form.propagateToMos,
    };
    saveMutation.mutate({ update, bearer: form.mosBearerToken });
  }

  function handleTest(): void {
    setTestResult(null);
    testMutation.mutate();
  }

  function handleDisableConfirm(): void {
    const bearer = form.mosBearerToken.trim();
    setConfirmDisableOpen(false);
    disableMutation.mutate(bearer);
  }

  function renderTestResult(): React.ReactNode {
    if (testMutation.isPending) {
      return <span className="text-xs text-text-secondary">Sending probe…</span>;
    }
    if (!testResult) return null;
    if (testResult.ok) {
      return (
        <span className="text-xs text-success">
          Probe sent → HTTP {testResult.httpStatus ?? '2xx'} ✓
        </span>
      );
    }
    const detail = testResult.error
      ? testResult.error
      : testResult.httpStatus
        ? `HTTP ${testResult.httpStatus}`
        : 'unknown error';
    return <span className="text-xs text-destructive">Failed: {detail}</span>;
  }

  return (
    <Card className="rounded-2xl border-border/70 bg-surface/95 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span>MOS Audit Federation</span>
          <StatusPill status={status} isLoading={isLoading} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-text-secondary">
          Forward Maestro audit events to MOS as the central audit sink. Configuration is stored in
          the local Vault module and the runtime forwarder reloads on save — no restart required.
        </p>

        <form onSubmit={handleSave} className="space-y-4">
          {/* Service ID */}
          <div className="space-y-1">
            <label htmlFor="mos-fed-service-id" className="text-xs font-medium text-text-primary">
              Service ID
            </label>
            <Input
              id="mos-fed-service-id"
              value={form.serviceId}
              onChange={e => {
                setForm(current => ({ ...current, serviceId: e.target.value }));
              }}
              placeholder={DEFAULT_SERVICE_ID}
              autoComplete="off"
              spellCheck={false}
              className="font-mono text-sm"
            />
            <p className="text-[11px] text-text-tertiary">
              3–64 chars matching <code>[a-zA-Z0-9_-]+</code>. Used as the lookup key on MOS and in
              outgoing HMAC headers.
            </p>
            {!serviceIdValid && form.serviceId.length > 0 && (
              <p className="text-[11px] text-destructive">
                Must be 3–64 chars and only letters / digits / dash / underscore.
              </p>
            )}
          </div>

          {/* HMAC Secret */}
          <div className="space-y-1">
            <label htmlFor="mos-fed-secret" className="text-xs font-medium text-text-primary">
              HMAC Secret
            </label>
            <div className="flex gap-1">
              <Input
                id="mos-fed-secret"
                type={showSecret ? 'text' : 'password'}
                value={form.hmacSecret}
                onChange={e => {
                  setForm(current => ({ ...current, hmacSecret: e.target.value }));
                  setJustGenerated(false);
                }}
                placeholder={
                  hasSecretOnServer
                    ? '(a secret is configured — enter a new value to replace)'
                    : '64 hex chars (32 bytes)'
                }
                autoComplete="new-password"
                spellCheck={false}
                className="flex-1 font-mono text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => {
                  setShowSecret(current => !current);
                }}
                aria-label={showSecret ? 'Hide secret' : 'Show secret'}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 gap-1"
                onClick={() => {
                  generateMutation.mutate();
                }}
                disabled={generateMutation.isPending}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Generate
              </Button>
            </div>
            {hasSecretOnServer && (
              <p className="text-[11px] text-text-tertiary">
                <KeyRound className="mr-1 inline h-3 w-3" />A secret is configured. Leave blank to
                keep it — the server requires a fresh secret on every save, so changing other fields
                means re-pasting or re-generating below.
              </p>
            )}
            {justGenerated && (
              <p className="text-[11px] text-warning">
                Generated — save now to persist; the secret is encrypted at rest. The value above is
                shown once and will not appear again after save.
              </p>
            )}
            {form.hmacSecret.length > 0 && !secretValid && (
              <p className="text-[11px] text-destructive">
                Must be hex-encoded and ≥ 32 characters (recommended: 64 hex / 32 bytes).
              </p>
            )}
          </div>

          {/* Forward URL */}
          <div className="space-y-1">
            <label htmlFor="mos-fed-forward-url" className="text-xs font-medium text-text-primary">
              Forward URL
            </label>
            <Input
              id="mos-fed-forward-url"
              value={form.forwardUrl}
              onChange={e => {
                setForm(current => ({ ...current, forwardUrl: e.target.value }));
              }}
              placeholder={DEFAULT_FORWARD_URL}
              autoComplete="off"
              spellCheck={false}
              className="text-sm"
            />
            {form.forwardUrl.length > 0 && !forwardUrlValid && (
              <p className="text-[11px] text-destructive">Must be a valid URL.</p>
            )}
          </div>

          {/* Auth Mode */}
          <div className="space-y-1">
            <label htmlFor="mos-fed-auth-mode" className="text-xs font-medium text-text-primary">
              Auth Mode
            </label>
            <select
              id="mos-fed-auth-mode"
              value={form.authMode}
              onChange={e => {
                const next = e.target.value === 'disabled' ? 'disabled' : 'mos-oidc';
                setForm(current => ({ ...current, authMode: next }));
              }}
              className="h-9 rounded-md border border-border bg-surface-elevated text-text-primary px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring [&>option]:bg-surface-elevated [&>option]:text-text-primary"
            >
              <option value="mos-oidc">MOS OIDC</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>

          {/* propagateToMos */}
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-xs text-text-primary">
              <input
                type="checkbox"
                checked={form.propagateToMos}
                onChange={e => {
                  setForm(current => ({ ...current, propagateToMos: e.target.checked }));
                }}
                className="h-4 w-4 rounded border-border"
              />
              Also register on MOS
            </label>
            <p className="text-[11px] text-text-tertiary">
              When enabled, the secret is also POSTed to MOS <code>/api/admin/service-tokens</code>{' '}
              so MOS can verify incoming HMAC headers.
            </p>
          </div>

          {/* MOS bearer token — only required when propagating */}
          {form.propagateToMos && (
            <div className="space-y-1">
              <label htmlFor="mos-fed-bearer" className="text-xs font-medium text-text-primary">
                MOS Operator Bearer Token
              </label>
              <Input
                id="mos-fed-bearer"
                type="password"
                value={form.mosBearerToken}
                onChange={e => {
                  setForm(current => ({ ...current, mosBearerToken: e.target.value }));
                }}
                placeholder="paste an operator JWT (sent only to MOS, not stored)"
                autoComplete="off"
                spellCheck={false}
                className="font-mono text-sm"
              />
              <p className="text-[11px] text-text-tertiary">
                Forwarded once to MOS to authenticate the registration call. Not stored locally;
                cleared after save.
              </p>
              {missingBearerForPropagation && (
                <p className="text-[11px] text-warning">
                  Required when “Also register on MOS” is checked.
                </p>
              )}
            </div>
          )}

          {/* Save / Test / Disable actions */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button type="submit" disabled={!canSave} className="gap-1">
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!status?.enabled || testMutation.isPending}
              onClick={handleTest}
              className="gap-1"
            >
              <PlayCircle className="h-4 w-4" />
              Test Federation
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!status?.enabled || disableMutation.isPending}
              onClick={() => {
                setConfirmDisableOpen(true);
              }}
              className="gap-1"
            >
              <Trash2 className="h-4 w-4" />
              {disableMutation.isPending ? 'Disabling…' : 'Disable'}
            </Button>
            <div className="ml-auto">{renderTestResult()}</div>
          </div>

          {/* Save feedback */}
          {saveError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              {saveError}
            </div>
          )}
          {saveSuccess && !saveError && (
            <div className="rounded-md border border-success/40 bg-success/10 p-2 text-xs text-success">
              Saved — runtime forwarder reloaded.
            </div>
          )}
        </form>
      </CardContent>

      <ConfirmDialog
        open={confirmDisableOpen}
        onOpenChange={setConfirmDisableOpen}
        title="Disable MOS audit federation?"
        description="Clears the local Vault config and tries (best-effort) to remove the matching service token from MOS. The runtime forwarder reloads immediately and stops sending events. You can re-enable this by saving the form again with a fresh secret."
        risk="high"
        confirmLabel="Disable"
        onConfirm={handleDisableConfirm}
      />
    </Card>
  );
}
